/**
 * Flights Autosuggest API
 *
 * Network-only functions for location/airport auto-suggest.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 */
import * as v from 'valibot';
import {
  type AutosuggestResponse,
  AutosuggestResponseSchema,
  type LocationSuggestion,
} from '@contracts';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildHeaders } from '@/api/utils';
import { generateLocationId } from '@/features/flights/utils/locationUtils';

/**
 * Transform API response item to UI-ready LocationSuggestion
 */
function transformToLocationSuggestion(
  item: AutosuggestResponse['results']['SUGGESTIONS']['data'][number],
): LocationSuggestion {
  return {
    id: generateLocationId(item.iata, item.cityName, item.countryCode),
    city: item.cityName,
    airportName: item.airportName,
    iataCode: item.iata,
    country: item.country,
    displayText: `${item.cityName}, ${item.country} (${item.iata}) - ${item.airportName}`,
  };
}

/**
 * Fetch auto-suggest results for flight location search
 * Returns suggestions based on the query
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 *
 * @param query - Search query (city name, airport code, etc.)
 * @returns Array of location suggestions
 */
export async function fetchFlightAutosuggest(query: string): Promise<LocationSuggestion[]> {
  // Return empty array if query is empty
  if (!query || query.trim() === '') {
    return [];
  }

  const url = FLIGHTS_ENDPOINTS.AUTOSUGGEST;
  const headers = buildHeaders();

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: unknown = await response.json();
  const validatedData = v.parse(AutosuggestResponseSchema, data);

  // Transform API response to UI-ready format
  const suggestions = validatedData.results.SUGGESTIONS.data.map(transformToLocationSuggestion);

  return suggestions;
}
